import { Component, ChangeDetectionStrategy, OnInit, inject, signal, input, output, effect, computed } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin } from 'rxjs';
import { StructuresService } from '../../../services/structures.service';
import { Field, StructureType, StructureTypeCreateDTO } from '../../../types/fields';
import { OcrPrimeStore } from '../../../store/ocr-prime.store';
import { StructureCardComponent } from './structure-card.component';
import { StructureDialogComponent, StructureDialogData, StructureDialogResult } from './structure-dialog.component';
import { StructureImportDialogComponent, StructureImportDialogResult } from './structure-import-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-structures-panel',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
    StructureCardComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4 h-full">
      <!-- Structures Header -->
      <div class="flex items-center justify-between pb-2 border-b border-zinc-200 dark:border-zinc-700">
        <div class="flex items-center gap-2">
          <mat-icon class="text-green-600 !text-xl">layers</mat-icon>
          <h2 data-testid="structures-panel-heading" class="text-lg font-semibold text-zinc-900 dark:text-white">Structures</h2>
          <span data-testid="structures-panel-count" class="ml-2 rounded-full bg-zinc-100 dark:bg-zinc-700 px-2.5 py-0.5 text-xs font-medium text-zinc-600 dark:text-zinc-300">
            {{ structures().length }}
          </span>
        </div>
        <div class="flex items-center gap-3">
          <button
            data-testid="structures-panel-import-btn"
            mat-mini-fab
            matTooltip="Import from JSON"
            (click)="openImportDialog()"
            class="!text-zinc-500 hover:!text-zinc-700 hover:!bg-zinc-100 dark:!text-zinc-400 dark:hover:!text-zinc-200 dark:!bg-zinc-700 dark:hover:!bg-zinc-600 !w-8 !h-8 !bg-zinc-50"
          >
            <mat-icon class="!text-lg">upload</mat-icon>
          </button>
          <button
            data-testid="structures-panel-export-btn"
            mat-mini-fab
            matTooltip="Export to Clipboard"
            (click)="exportToClipboard()"
            [disabled]="structures().length === 0"
            class="!text-zinc-500 hover:!text-zinc-700 hover:!bg-zinc-100 dark:!text-zinc-400 dark:hover:!text-zinc-200 dark:!bg-zinc-700 dark:hover:!bg-zinc-600 disabled:!text-zinc-300 !w-8 !h-8 !bg-zinc-50"
          >
            <mat-icon class="!text-lg">download</mat-icon>
          </button>
          <button
            data-testid="structures-panel-add-structure-btn"
            mat-flat-button
            color="primary"
            class="gap-1.5 shadow-sm !h-8 !text-sm"
            (click)="openCreateStructureDialog()"
          >
            <mat-icon class="!text-lg">add</mat-icon>
            Add Structure
          </button>
        </div>
      </div>

      <!-- Search -->
      <div class="relative">
        <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500 !text-lg pointer-events-none">search</mat-icon>
        <input
          data-testid="structures-panel-search-input"
          type="text"
          placeholder="Search structures or their fields..."
          [value]="searchTerm()"
          (input)="onSearchChange($event)"
          class="w-full pl-10 pr-4 py-2 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-zinc-100 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all dark:placeholder:text-zinc-500"
        >
      </div>

      <!-- Structures List -->
      @if (loadingStructures()) {
        <div class="flex justify-center py-16">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else if (filteredStructures().length === 0) {
        <div class="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800/50 p-8 text-center h-64">
          <div class="mb-3 rounded-full bg-zinc-100 dark:bg-zinc-700 p-3">
            <mat-icon class="text-zinc-400 dark:text-zinc-500 !text-2xl !w-7 !h-7 !leading-7">layers</mat-icon>
          </div>
          @if (searchTerm()) {
            <h3 data-testid="structures-panel-no-results" class="text-sm font-medium text-zinc-900 dark:text-zinc-100">No structures found</h3>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 max-w-[220px]">
              No structures match "{{ searchTerm() }}".
            </p>
            <button
              mat-stroked-button
              class="mt-4 !text-sm"
              (click)="clearSearch()"
            >
              Clear search
            </button>
          } @else {
            <h3 class="text-sm font-medium text-zinc-900 dark:text-zinc-100">No structures yet</h3>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 max-w-[220px]">Structures group fields into reusable document layouts — templates extract data based on them.</p>
            <button
              mat-stroked-button
              class="mt-4 !text-sm"
              (click)="openCreateStructureDialog()"
            >
              Add Structure
            </button>
          }
        </div>
      } @else {
        <div class="flex-1 space-y-3 overflow-y-auto max-h-[calc(100vh-360px)] pr-1 -mr-1">
          @for (structure of filteredStructures(); track structure.id) {
            <app-structure-card
              class="block"
              [structure]="structure"
              [allFields]="allFields()"
              [isDeleting]="deletingStructureId() === structure.id"
              (edit)="openEditStructureDialog($event)"
              (delete)="deleteStructure($event)"
              (hovered)="structureHovered.emit($event)"
            />
          }
        </div>
        <div class="text-center text-xs text-zinc-400 dark:text-zinc-500 pt-1">
          {{ filteredStructures().length }} {{ filteredStructures().length === 1 ? 'structure' : 'structures' }}
          @if (searchTerm()) {
            <span> matching "{{ searchTerm() }}"</span>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `]
})
export class StructuresPanelComponent implements OnInit {
  private structuresService = inject(StructuresService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  readonly store = inject(OcrPrimeStore);

  allFields = input<Field[]>([]);
  /** When set, the panel will auto-open the edit dialog for this structure once loaded. */
  autoEditStructureId = input<string | null>(null);

  /** Emits the hovered structure (or null) so sibling panels can highlight its fields. */
  structureHovered = output<StructureType | null>();

  structures = computed(() => this.store.structures());
  loadingStructures = computed(() => this.store.structuresLoading());
  deletingStructureId = signal<string | null>(null);
  searchTerm = signal('');

  filteredStructures = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term) return this.structures();
    const fieldsById = new Map(this.allFields().map(f => [f.id, f]));
    return this.structures().filter(s =>
      s.name.toLowerCase().includes(term) ||
      s.description?.toLowerCase().includes(term) ||
      s.fieldIds.some(id => fieldsById.get(id)?.name.toLowerCase().includes(term))
    );
  });

  private autoEditTriggered = false;

  constructor() {
    // Watch for structures loaded + autoEditStructureId to auto-open dialog
    effect(() => {
      const id = this.autoEditStructureId();
      const list = this.structures();
      if (id && list.length > 0 && !this.autoEditTriggered) {
        const target = list.find(s => s.id === id);
        if (target) {
          this.autoEditTriggered = true;
          // Defer to avoid change-detection issues
          setTimeout(() => this.openEditStructureDialog(target), 0);
        }
      }
    });
  }

  ngOnInit(): void {
    this.loadStructures();
  }

  loadStructures(): void {
    this.store.loadStructures(true);
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  clearSearch(): void {
    this.searchTerm.set('');
  }

  openCreateStructureDialog(): void {
    this.openStructureDialog({ mode: 'create', allFields: this.allFields() });
  }

  openEditStructureDialog(structure: StructureType): void {
    this.openStructureDialog({ mode: 'edit', structure, allFields: this.allFields() });
  }

  private openStructureDialog(data: StructureDialogData): void {
    // Right-side drawer — consistent with the field editor side panel.
    const dialogRef = this.dialog.open(StructureDialogComponent, {
      data,
      width: 'min(640px, 95vw)',
      maxWidth: '95vw',
      height: '100dvh',
      maxHeight: '100dvh',
      position: { right: '0', top: '0' },
      panelClass: 'field-drawer-panel'
    });

    dialogRef.afterClosed().subscribe((result: StructureDialogResult | undefined) => {
      if (!result || result.action === 'cancel') return;

      if (data.mode === 'edit' && result.structureId && result.data) {
        this.structuresService.updateStructure(result.structureId, result.data).subscribe({
          next: () => {
            this.snackBar.open('Structure updated successfully', 'Close', { duration: 2000 });
            this.loadStructures();
          },
          error: (err) => {
            console.error('Error updating structure:', err);
            this.snackBar.open('Error updating structure', 'Close', { duration: 3000 });
          }
        });
      } else if (result.data) {
        this.structuresService.createStructure(result.data).subscribe({
          next: () => {
            this.snackBar.open('Structure created successfully', 'Close', { duration: 2000 });
            this.loadStructures();
          },
          error: (err) => {
            console.error('Error creating structure:', err);
            this.snackBar.open('Error creating structure', 'Close', { duration: 3000 });
          }
        });
      }
    });
  }

  async exportToClipboard(): Promise<void> {
    const structuresToExport = this.structures().map(structure => ({
      id: structure.id,
      name: structure.name,
      description: structure.description || '',
      fieldIds: structure.fieldIds,
      color: structure.color
    } as StructureTypeCreateDTO));

    try {
      const json = JSON.stringify(structuresToExport, null, 2);
      await navigator.clipboard.writeText(json);
      this.snackBar.open(`${structuresToExport.length} structure(s) copied to clipboard`, 'Close', { duration: 2000 });
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      this.snackBar.open('Error copying to clipboard', 'Close', { duration: 3000 });
    }
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open(StructureImportDialogComponent, {
      width: '500px',
      panelClass: 'field-dialog-panel'
    });

    dialogRef.afterClosed().subscribe((result: StructureImportDialogResult | undefined) => {
      if (!result || result.action === 'cancel' || !result.structures?.length) return;

      const createRequests = result.structures.map(structure =>
        this.structuresService.createStructure(structure)
      );

      forkJoin(createRequests).subscribe({
        next: () => {
          this.snackBar.open(`${result.structures!.length} structure(s) imported successfully`, 'Close', { duration: 2000 });
          this.loadStructures();
        },
        error: (err) => {
          console.error('Error importing structures:', err);
          this.snackBar.open('Error importing structures. Some structures may have been created.', 'Close', { duration: 3000 });
          this.loadStructures();
        }
      });
    });
  }

  deleteStructure(structure: StructureType): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      panelClass: 'field-dialog-panel',
      data: {
        title: `Delete structure "${structure.name}"?`,
        message: 'This action cannot be undone. Consider renaming the structure (e.g., adding a "legacy" suffix) instead of deleting it.',
        consequences: [
          'Documents processed with this structure will have broken history references.',
          'Templates using this structure will need to be reconfigured.'
        ],
        confirmLabel: 'Delete Structure',
        requireNameMatch: structure.name
      } as ConfirmDialogData
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.deletingStructureId.set(structure.id);
      this.structuresService.deleteStructure(structure.id).subscribe({
        next: () => {
          this.snackBar.open('Structure deleted successfully', 'Close', { duration: 2000 });
          this.store.removeStructureLocally(structure.id);
          this.deletingStructureId.set(null);
        },
        error: (err) => {
          console.error('Error deleting structure:', err);
          this.snackBar.open('Error deleting structure', 'Close', { duration: 3000 });
          this.deletingStructureId.set(null);
        }
      });
    });
  }
}
